import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {forkJoin, Observable, of} from 'rxjs';
import {BaseChartDirective} from 'ng2-charts';
import {ChartData, ChartOptions} from 'chart.js';
import {MetaTitle} from '../../../../decorator/meta-title';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {StatsFiltersComponent} from '../stats-filters/stats-filters.component';
import {StatisticsService} from '../../../../services/statistics/statistics.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';
import {FileDownloadService} from '../../../../services/file-download/file-download.service';
import {StatsTransactionDates} from '../../../../constants/stats-transaction-dates';
import {
  StatsFilterUrlService,
  StatsPeriodParam,
  StatsPeriodParamNames
} from '../../../../services/stats-filter-url/stats-filter-url.service';
import {
  addSums,
  createEmptySums,
  HourlyDefaultMetricKeys,
  HourlyMetric,
  HourlyMetricGroup,
  HourlyMetricGroups,
  HourlyMetrics,
  HourlySums,
  scaleSums,
  toSums,
} from './hourly-metrics';

type HourlyMode = 'timeline' | 'profile';
type HourlyValueMode = 'total' | 'average';
type HourlyComparison = 'none' | 'previous' | 'last-week' | 'last-year' | 'custom';
type HourlySlotState = 'past' | 'current' | 'future';

interface HourlyPeriod {
  start: string;
  end: string;
}

interface HourlyCell {
  a: string;
  b: string;
  delta: string;
  trend: 'good' | 'bad' | 'flat' | null;
  aValue: number | null;
  bValue: number | null;
  deltaValue: number | null;
}

interface HourlySlot {
  dayIndex: number;
  hour: number;
  dateA: string | null;
  dateB: string | null;
  a: HourlySums | null;
  b: HourlySums | null;
  state: HourlySlotState;
  cells: Record<string, HourlyCell>;
}

interface HourlyDay {
  dayIndex: number;
  dateA: string | null;
  dateB: string | null;
  a: HourlySums;
  b: HourlySums;
  partial: boolean;
  slots: HourlySlot[];
  cells: Record<string, HourlyCell>;
}

interface HourlyTotal {
  label: string;
  hint: string;
  a: HourlySums;
  b: HourlySums;
  compared: boolean;
  cells: Record<string, HourlyCell>;
}

interface HourlyPreferences {
  metrics?: string[];
  chartMetric?: string;
}

const DAY_MS = 86400000;
const PARIS_ZONE = 'Europe/Paris';
const PREFERENCES_KEY = 'stats-hourly:preferences';
const DEFAULT_PERIOD = 'today';
const DEFAULT_COMPARED_PERIOD = 'yesterday';
const DEFAULT_MODE: HourlyMode = 'timeline';
const DEFAULT_COMPARISON: HourlyComparison = 'last-week';
const COMPARISONS: HourlyComparison[] = ['none', 'previous', 'last-week', 'last-year', 'custom'];
const COMPARED_PERIOD_NAMES: StatsPeriodParamNames = {
  period: 'comparePeriod',
  start: 'compareStartDate',
  end: 'compareEndDate',
};

const MONEY_FORMAT = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'});
const MONEY_AXIS_FORMAT = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR', maximumFractionDigits: 0});
const PERCENT_FORMAT = new Intl.NumberFormat('fr-FR', {style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2});
const DELTA_FORMAT = new Intl.NumberFormat('fr-FR', {style: 'percent', maximumFractionDigits: 1});
const COUNT_FORMAT = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 0});
const DECIMAL_FORMAT = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 1});
const POINT_FORMAT = new Intl.NumberFormat('fr-FR', {maximumFractionDigits: 2});

@Component({
  selector: 'app-hourly',
  imports: [
    FormsModule,
    NgClass,
    NgTemplateOutlet,
    BaseChartDirective,
    DateRangeSelectorComponent,
    StatsFiltersComponent,
  ],
  templateUrl: './hourly.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './hourly.component.scss'
})
@MetaTitle('Hourly statistics')
export class HourlyComponent implements OnInit {
  @ViewChild(StatsFiltersComponent, {static: true}) statsFilters!: StatsFiltersComponent;

  metricGroups = HourlyMetricGroups;
  metrics = HourlyMetrics;
  transactionDates = StatsTransactionDates;

  maxDays = 14;
  initialParams: ParamMap;
  initialPeriod: StatsPeriodParam;
  initialComparedPeriod: StatsPeriodParam;

  mode: HourlyMode = DEFAULT_MODE;
  valueMode: HourlyValueMode = 'total';
  comparison: HourlyComparison = DEFAULT_COMPARISON;
  transactionDate: string | null = null;

  period: HourlyPeriod | null = null;
  comparedPeriod: HourlyPeriod | null = null;
  periodError: string | null = null;

  selectedMetricKeys: string[] = [...HourlyDefaultMetricKeys];
  chartMetricKey = 'leadOk';

  loading = false;
  loaded = false;

  appliedMode: HourlyMode = 'timeline';
  appliedPeriod: HourlyPeriod | null = null;
  appliedComparedPeriod: HourlyPeriod | null = null;
  appliedCriteria: any = {};
  partialProfile = false;

  slots: HourlySlot[] = [];
  days: HourlyDay[] = [];
  totals: HourlyTotal[] = [];

  chartData: ChartData<'line'> = {labels: [], datasets: []};
  chartOptions: ChartOptions<'line'> = this.createChartOptions();

  private customComparedPeriod: HourlyPeriod | null = null;
  private periodSelector: DateRangeSelectorComponent | null = null;
  private comparedPeriodSelector: DateRangeSelectorComponent | null = null;
  private currentHourLabel = '';

  constructor(
    private statisticsService: StatisticsService,
    private auth: AuthService,
    private userActivity: UserActivityService,
    private fileDownload: FileDownloadService,
    protected loader: LoaderService,
    protected route: ActivatedRoute,
    private urlFilters: StatsFilterUrlService,
  ) {
    this.loadPreferences();

    this.initialParams = this.urlFilters.getInitialParams(this.route);
    this.initialPeriod = this.urlFilters.getPeriod(this.initialParams, DEFAULT_PERIOD);
    this.initialComparedPeriod = this.urlFilters.getPeriod(this.initialParams, DEFAULT_COMPARED_PERIOD, COMPARED_PERIOD_NAMES);

    const mode = this.initialParams.get('view');
    if (mode === 'timeline' || mode === 'profile') {
      this.mode = mode;
    }

    if (this.mode === 'profile' && this.initialParams.get('values') === 'average') {
      this.valueMode = 'average';
    }

    const comparison = this.initialParams.get('compare') as HourlyComparison;
    if (COMPARISONS.includes(comparison)) {
      this.comparison = comparison;
    }

    const transactionDate = this.initialParams.get('transactionDate');
    if (this.transactionDates.some(option => option.value === transactionDate)) {
      this.transactionDate = transactionDate;
    }
  }

  ngOnInit(): void {
    this.auth.getAsyncConfig().subscribe(config => {
      this.maxDays = Number(config?.data?.hourlyStatisticsMaxDays) || this.maxDays;
      this.refreshPeriods();
    });
  }

  onPeriodChange(selector: DateRangeSelectorComponent): void {
    this.periodSelector = selector;
    this.period = this.readPeriod(selector);
    this.refreshPeriods();
  }

  onComparedPeriodChange(selector: DateRangeSelectorComponent): void {
    this.comparedPeriodSelector = selector;
    this.customComparedPeriod = this.readPeriod(selector);
    this.refreshPeriods();
  }

  getDayCount(period: HourlyPeriod | null): number {
    return period ? this.daysBetween(period.start, period.end) + 1 : 0;
  }

  formatPeriod(period: HourlyPeriod | null): string {
    if (!period) {
      return '';
    }

    const start = this.formatDay(period.start);

    return period.start === period.end ? start : start + ' → ' + this.formatDay(period.end);
  }

  isComparing(): boolean {
    return this.appliedComparedPeriod !== null;
  }

  isTimeline(): boolean {
    return this.appliedMode === 'timeline';
  }

  isAverage(): boolean {
    return !this.isTimeline() && this.valueMode === 'average';
  }

  hasCost(): boolean {
    return !this.appliedCriteria.country && !this.appliedCriteria.lang;
  }

  getMetricsOfGroup(group: HourlyMetricGroup): HourlyMetric[] {
    return this.metrics.filter(metric => metric.group === group);
  }

  getVisibleMetrics(): HourlyMetric[] {
    return this.metrics.filter(metric =>
      this.selectedMetricKeys.includes(metric.key) && (!metric.costBased || this.hasCost())
    );
  }

  getChartMetrics(): HourlyMetric[] {
    return this.metrics.filter(metric => !metric.costBased || this.hasCost());
  }

  isMetricSelected(metric: HourlyMetric): boolean {
    return this.selectedMetricKeys.includes(metric.key);
  }

  toggleMetric(metric: HourlyMetric): void {
    if (this.isMetricSelected(metric)) {
      this.selectedMetricKeys = this.selectedMetricKeys.filter(key => key !== metric.key);
    } else {
      this.selectedMetricKeys = [...this.selectedMetricKeys, metric.key];
    }

    this.savePreferences();
  }

  resetMetrics(): void {
    this.selectedMetricKeys = [...HourlyDefaultMetricKeys];
    this.savePreferences();
  }

  selectChartMetric(metric: HourlyMetric): void {
    this.chartMetricKey = metric.key;
    this.savePreferences();
    this.refreshChart();
  }

  changeChartMetric(): void {
    this.savePreferences();
    this.refreshChart();
  }

  changeValueMode(): void {
    this.refreshCells();
  }

  changeMode(): void {
    if (this.mode === 'timeline') {
      this.valueMode = 'total';
    }

    this.refreshPeriods();
  }

  changeComparison(): void {
    this.refreshPeriods();
  }

  saveFilters(): void {
    this.urlFilters.save(this.route, this.getUrlParams());
    this.apply();
  }

  clearFilters(): void {
    this.urlFilters.clear(this.route);
    this.mode = DEFAULT_MODE;
    this.valueMode = 'total';
    this.comparison = DEFAULT_COMPARISON;
    this.transactionDate = null;
    this.statsFilters.reset();

    if (this.periodSelector) {
      this.urlFilters.resetPeriod(this.periodSelector, DEFAULT_PERIOD);
    }

    if (this.comparedPeriodSelector) {
      this.urlFilters.resetPeriod(this.comparedPeriodSelector, DEFAULT_COMPARED_PERIOD);
    }

    this.refreshPeriods();
    this.apply();
  }

  apply(): void {
    this.refreshPeriods();

    if (this.periodError || !this.period) {
      return;
    }

    const period = this.period;
    const comparedPeriod = this.comparedPeriod;
    const mode = this.mode;

    const criteria: any = {
      groupBy: mode === 'timeline' ? 'hour' : 'hour-of-day',
      ...this.statsFilters.getCriteria(),
    };

    if (this.transactionDate) {
      criteria.transactionDate = this.transactionDate;
    }

    const params = this.createParams(criteria, period);
    const comparedParams = comparedPeriod ? this.createParams(criteria, comparedPeriod) : null;

    this.loading = true;
    this.loader.startLoading();
    this.userActivity.reportFilters({
      ...params,
      ...(comparedPeriod ? {compareStartDate: comparedPeriod.start, compareEndDate: comparedPeriod.end} : {}),
    });

    const request = this.statisticsService.getDetailed(params);
    const comparedRequest: Observable<any> = comparedParams ? this.statisticsService.getDetailed(comparedParams) : of(null);

    forkJoin([request, comparedRequest]).subscribe({
      next: ([data, comparedData]: any[]) => {
        this.appliedMode = mode;
        this.appliedPeriod = period;
        this.appliedComparedPeriod = comparedPeriod;
        this.appliedCriteria = criteria;

        if (mode === 'profile' && comparedPeriod && this.getDayCount(period) !== this.getDayCount(comparedPeriod)) {
          this.valueMode = 'average';
        }

        this.urlFilters.update(this.route, this.getUrlParams());
        this.buildSlots(data?.rows ?? [], comparedData?.rows ?? []);
        this.loading = false;
        this.loaded = true;
        this.loader.stopLoading();
      },
      error: () => {
        this.loading = false;
        this.loader.stopLoading();
      }
    });
  }

  getHourLabel(slot: HourlySlot): string {
    return String(slot.hour).padStart(2, '0') + ':00';
  }

  getSlotTitle(slot: HourlySlot): string {
    const hour = this.getHourLabel(slot);
    const a = slot.dateA ? this.formatDay(slot.dateA) + ' ' + hour : '—';

    if (!this.isComparing()) {
      return a;
    }

    const b = slot.dateB ? this.formatDay(slot.dateB) + ' ' + hour : '—';

    return 'A: ' + a + '\nB: ' + b;
  }

  getDayLabel(day: HourlyDay): string {
    return day.dateA ? this.formatDay(day.dateA) : 'Day ' + (day.dayIndex + 1);
  }

  getComparedDayLabel(day: HourlyDay): string {
    return day.dateB ? this.formatDay(day.dateB) : '';
  }

  getStateTitle(slot: HourlySlot): string {
    if (slot.state === 'current') {
      return 'Hour in progress';
    }

    return slot.state === 'future' ? 'Not reached yet' : '';
  }

  getMetricColspan(): number {
    return this.isComparing() ? 3 : 1;
  }

  getTableColspan(): number {
    return 1 + this.getVisibleMetrics().length * this.getMetricColspan();
  }

  getCurrentHourLabel(): string {
    return this.currentHourLabel;
  }

  downloadCsv(): void {
    if (!this.loaded || !this.slots.length) {
      return;
    }

    const metrics = this.getVisibleMetrics();
    const headers = ['Date', 'Hour'];

    if (this.isComparing()) {
      headers.push('Compared date');
    }

    for (const metric of metrics) {
      if (this.isComparing()) {
        headers.push(metric.label + ' A', metric.label + ' B', metric.label + ' Δ');
      } else {
        headers.push(metric.label);
      }
    }

    const lines: string[] = [headers.map(header => this.csvCell(header)).join(',')];

    for (const slot of this.slots) {
      lines.push(this.buildCsvLine(
        [this.isTimeline() ? (slot.dateA ?? '') : '', this.getHourLabel(slot)],
        this.isTimeline() ? (slot.dateB ?? '') : '',
        slot.cells,
        metrics
      ));
    }

    for (const total of this.totals) {
      lines.push(this.buildCsvLine([total.label, ''], '', total.cells, metrics));
    }

    this.fileDownload.saveCsv(lines.join('\n'), 'hourly-statistics.csv');
  }

  private getUrlParams(): any {
    const params: any = {
      ...(this.periodSelector ? this.urlFilters.getPeriodParams(this.periodSelector) : {}),
      view: this.mode,
      values: this.mode === 'profile' ? this.valueMode : null,
      compare: this.comparison,
      transactionDate: this.transactionDate,
      ...this.statsFilters.getCriteria(),
    };

    if (this.comparison === 'custom' && this.comparedPeriodSelector) {
      Object.assign(params, this.urlFilters.getPeriodParams(this.comparedPeriodSelector, COMPARED_PERIOD_NAMES));
    }

    return params;
  }

  private readPeriod(selector: DateRangeSelectorComponent): HourlyPeriod | null {
    if (!selector.dateFromSelector || !selector.dateToSelector) {
      return null;
    }

    return {start: selector.dateFromSelector, end: selector.dateToSelector};
  }

  private refreshPeriods(): void {
    this.comparedPeriod = this.computeComparedPeriod();
    this.periodError = this.computePeriodError();
  }

  private computeComparedPeriod(): HourlyPeriod | null {
    const period = this.period;

    if (!period) {
      return null;
    }

    switch (this.comparison) {
      case 'previous':
        return {
          start: this.addDays(period.start, -this.getDayCount(period)),
          end: this.addDays(period.start, -1),
        };
      case 'last-week':
        return {start: this.addDays(period.start, -7), end: this.addDays(period.end, -7)};
      case 'last-year':
        return {start: this.addDays(period.start, -364), end: this.addDays(period.end, -364)};
      case 'custom':
        return this.customComparedPeriod;
    }

    return null;
  }

  private computePeriodError(): string | null {
    if (!this.period) {
      return 'Select a period.';
    }

    if (this.comparison === 'custom' && !this.comparedPeriod) {
      return 'Select the period to compare with.';
    }

    if (this.mode !== 'timeline') {
      return null;
    }

    if (this.getDayCount(this.period) > this.maxDays || this.getDayCount(this.comparedPeriod) > this.maxDays) {
      return 'The hour by hour view is limited to ' + this.maxDays + ' days per period. '
        + 'Shorten the period or switch to the 24h profile.';
    }

    return null;
  }

  private createParams(criteria: any, period: HourlyPeriod): any {
    return {
      ...criteria,
      startDate: period.start + 'T00:00:00',
      endDate: period.end + 'T23:59:59',
    };
  }

  private buildSlots(rows: any[], comparedRows: any[]): void {
    const period = this.appliedPeriod as HourlyPeriod;
    const comparedPeriod = this.appliedComparedPeriod;
    const slotsByPosition = new Map<string, HourlySlot>();
    const now = this.getParisNow();

    this.currentHourLabel = String(now.hour).padStart(2, '0') + ':00';

    const getSlot = (dayIndex: number, hour: number): HourlySlot => {
      const position = dayIndex + '|' + hour;
      let slot = slotsByPosition.get(position);

      if (!slot) {
        slot = {
          dayIndex,
          hour,
          dateA: this.getDateAt(period, dayIndex),
          dateB: comparedPeriod ? this.getDateAt(comparedPeriod, dayIndex) : null,
          a: null,
          b: null,
          state: 'past',
          cells: {},
        };
        slotsByPosition.set(position, slot);
      }

      return slot;
    };

    if (this.isTimeline()) {
      for (const row of rows) {
        const [date, hour] = String(row.key).split(' ');
        getSlot(this.daysBetween(period.start, date), Number(hour)).a = toSums(row);
      }

      for (const row of comparedRows) {
        const [date, hour] = String(row.key).split(' ');
        getSlot(this.daysBetween((comparedPeriod as HourlyPeriod).start, date), Number(hour)).b = toSums(row);
      }
    } else {
      for (let hour = 0; hour < 24; hour++) {
        getSlot(0, hour);
      }

      for (const row of rows) {
        getSlot(0, Number(row.key)).a = toSums(row);
      }

      for (const row of comparedRows) {
        getSlot(0, Number(row.key)).b = toSums(row);
      }
    }

    this.slots = [...slotsByPosition.values()].sort((x, y) => x.dayIndex - y.dayIndex || x.hour - y.hour);

    const singleDay = period.start === period.end;
    this.partialProfile = !this.isTimeline() && !singleDay && period.start <= now.date && now.date <= period.end;

    for (const slot of this.slots) {
      const date = this.isTimeline() ? slot.dateA : (singleDay ? period.start : null);
      slot.state = this.getSlotState(date, slot.hour, now);

      if (slot.state === 'future') {
        slot.a = null;
      }
    }

    this.days = [];

    if (this.isTimeline()) {
      for (const slot of this.slots) {
        let day = this.days[this.days.length - 1];

        if (!day || day.dayIndex !== slot.dayIndex) {
          day = {
            dayIndex: slot.dayIndex,
            dateA: slot.dateA,
            dateB: slot.dateB,
            a: createEmptySums(),
            b: createEmptySums(),
            partial: false,
            slots: [],
            cells: {},
          };
          this.days.push(day);
        }

        day.slots.push(slot);
      }

      for (const day of this.days) {
        day.partial = day.slots.some(slot => slot.state !== 'past');
        this.addSlotSums(day.a, day.b, day.slots.filter(slot => !day.partial || slot.state === 'past'));
      }
    }

    this.buildTotals();
    this.refreshCells();
  }

  private buildTotals(): void {
    const hasCutoff = this.slots.some(slot => slot.state !== 'past');
    const total = (slots: HourlySlot[]) => {
      const a = createEmptySums();
      const b = createEmptySums();
      this.addSlotSums(a, b, slots);

      return {a, b};
    };

    if (!hasCutoff || !this.isComparing()) {
      const whole = total(this.slots);
      this.totals = [{label: 'Total', hint: '', ...whole, compared: this.isComparing(), cells: {}}];

      return;
    }

    const completed = total(this.slots.filter(slot => slot.state === 'past'));
    const whole = total(this.slots);

    this.totals = [
      {
        label: 'Until ' + this.currentHourLabel,
        hint: 'Completed hours only, on both periods: the fair comparison while the period is still running',
        ...completed,
        compared: true,
        cells: {},
      },
      {
        label: 'Whole period',
        hint: 'Everything recorded so far on the period, against the full compared period',
        ...whole,
        compared: false,
        cells: {},
      },
    ];
  }

  private addSlotSums(a: HourlySums, b: HourlySums, slots: HourlySlot[]): void {
    for (const slot of slots) {
      if (slot.a) {
        addSums(a, slot.a);
      }

      if (slot.b) {
        addSums(b, slot.b);
      }
    }
  }

  private refreshCells(): void {
    const divisorA = this.isAverage() ? this.getDayCount(this.appliedPeriod) : 1;
    const divisorB = this.isAverage() ? this.getDayCount(this.appliedComparedPeriod) : 1;

    for (const slot of this.slots) {
      slot.cells = this.buildCells(slot.a, slot.b, divisorA, divisorB, true);
    }

    for (const day of this.days) {
      day.cells = this.buildCells(day.a, day.b, 1, 1, true);
    }

    for (const total of this.totals) {
      total.cells = this.buildCells(total.a, total.b, divisorA, divisorB, total.compared);
    }

    this.refreshChart();
  }

  private buildCells(
    a: HourlySums | null,
    b: HourlySums | null,
    divisorA: number,
    divisorB: number,
    compared: boolean
  ): Record<string, HourlyCell> {
    const cells: Record<string, HourlyCell> = {};
    const scaledA = a ? scaleSums(a, divisorA) : null;
    const scaledB = b && this.isComparing() ? scaleSums(b, divisorB) : null;

    for (const metric of this.metrics) {
      const aValue = scaledA ? metric.compute(scaledA) : null;
      const bValue = scaledB ? metric.compute(scaledB) : null;
      const delta = compared ? this.computeDelta(metric, aValue, bValue) : null;

      cells[metric.key] = {
        a: this.formatValue(metric, aValue),
        b: this.isComparing() ? this.formatValue(metric, bValue) : '',
        delta: compared ? this.formatDelta(metric, delta) : '',
        trend: compared ? this.getTrend(metric, delta) : null,
        aValue,
        bValue,
        deltaValue: delta,
      };
    }

    return cells;
  }

  private computeDelta(metric: HourlyMetric, a: number | null, b: number | null): number | null {
    if (a === null || b === null) {
      return null;
    }

    if (metric.format === 'percent') {
      return a - b;
    }

    if (b === 0) {
      return a === 0 ? 0 : null;
    }

    return (a - b) / Math.abs(b);
  }

  private getTrend(metric: HourlyMetric, delta: number | null): HourlyCell['trend'] {
    if (delta === null) {
      return null;
    }

    if (Math.abs(delta) < 0.0005) {
      return 'flat';
    }

    return (delta > 0) !== !!metric.lowerIsBetter ? 'good' : 'bad';
  }

  private formatValue(metric: HourlyMetric, value: number | null): string {
    if (value === null) {
      return '—';
    }

    switch (metric.format) {
      case 'money':
        return MONEY_FORMAT.format(value / 100);
      case 'percent':
        return PERCENT_FORMAT.format(value);
      default:
        return (this.isAverage() ? DECIMAL_FORMAT : COUNT_FORMAT).format(value);
    }
  }

  private formatDelta(metric: HourlyMetric, delta: number | null): string {
    if (delta === null) {
      return 'n/a';
    }

    const sign = delta > 0 ? '+' : '';

    if (metric.format === 'percent') {
      return sign + POINT_FORMAT.format(delta * 100) + ' pt';
    }

    return sign + DELTA_FORMAT.format(delta);
  }

  private refreshChart(): void {
    const metric = this.metrics.find(item => item.key === this.chartMetricKey) ?? this.metrics[0];
    const toChartValue = (value: number | null) => {
      if (value === null) {
        return null;
      }

      if (metric.format === 'money') {
        return value / 100;
      }

      return metric.format === 'percent' ? value * 100 : value;
    };

    const labels = this.slots.map(slot => this.getHourLabel(slot));

    const datasets: ChartData<'line'>['datasets'] = [{
      label: metric.label + ' — ' + this.formatPeriod(this.appliedPeriod),
      data: this.slots.map(slot => slot.state === 'future' ? null : toChartValue(slot.cells[metric.key]?.aValue ?? null)),
      borderColor: '#0f766e',
      backgroundColor: 'rgba(15, 118, 110, 0.08)',
      pointRadius: this.slots.length > 48 ? 0 : 3,
      borderWidth: 2,
      fill: true,
      tension: 0.25,
    }];

    if (this.isComparing()) {
      datasets.push({
        label: metric.label + ' — ' + this.formatPeriod(this.appliedComparedPeriod),
        data: this.slots.map(slot => toChartValue(slot.cells[metric.key]?.bValue ?? null)),
        borderColor: '#d97706',
        backgroundColor: 'rgba(217, 119, 6, 0.1)',
        borderDash: [6, 4],
        pointRadius: this.slots.length > 48 ? 0 : 3,
        borderWidth: 2,
        fill: false,
        tension: 0.25,
      });
    }

    this.chartData = {labels, datasets};
    this.chartOptions = this.createChartOptions(metric);
  }

  private createChartOptions(metric: HourlyMetric | null = null): ChartOptions<'line'> {
    const format = (value: number) => {
      if (metric?.format === 'money') {
        return MONEY_AXIS_FORMAT.format(value);
      }

      if (metric?.format === 'percent') {
        return POINT_FORMAT.format(value) + ' %';
      }

      return DECIMAL_FORMAT.format(value);
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {mode: 'index', intersect: false},
      plugins: {
        legend: {position: 'top', align: 'end'},
        tooltip: {
          callbacks: {
            title: items => {
              const slot = this.slots[items[0]?.dataIndex ?? -1];

              return slot && this.isTimeline() ? this.getSlotTitle(slot).split('\n') : items[0]?.label ?? '';
            },
            label: context => ' ' + context.dataset.label + ' : '
              + (context.parsed.y === null ? '—' : format(context.parsed.y)),
          }
        },
      },
      scales: {
        x: {grid: {display: false}, ticks: {autoSkip: true, maxRotation: 0}},
        y: {beginAtZero: true, ticks: {callback: value => format(Number(value))}},
      },
    };
  }

  private buildCsvLine(prefix: string[], comparedDate: string, cells: Record<string, HourlyCell>, metrics: HourlyMetric[]): string {
    const values: string[] = prefix.map(value => this.csvCell(value));

    if (this.isComparing()) {
      values.push(this.csvCell(comparedDate));
    }

    for (const metric of metrics) {
      const cell = cells[metric.key];
      values.push(this.csvNumber(metric, cell?.aValue ?? null));

      if (this.isComparing()) {
        values.push(this.csvNumber(metric, cell?.bValue ?? null));
        values.push(cell?.deltaValue === null || cell?.deltaValue === undefined ? '' : (cell.deltaValue * 100).toFixed(2));
      }
    }

    return values.join(',');
  }

  private csvNumber(metric: HourlyMetric, value: number | null): string {
    if (value === null) {
      return '';
    }

    if (metric.format === 'money') {
      return (value / 100).toFixed(2);
    }

    if (metric.format === 'percent') {
      return (value * 100).toFixed(2);
    }

    return String(Math.round(value * 10) / 10);
  }

  private csvCell(value: string): string {
    return `"${value.replace(/"/g, '""')}"`;
  }

  private getSlotState(date: string | null, hour: number, now: {date: string, hour: number}): HourlySlotState {
    if (date === null || date < now.date) {
      return 'past';
    }

    if (date > now.date || hour > now.hour) {
      return 'future';
    }

    return hour === now.hour ? 'current' : 'past';
  }

  private getParisNow(): {date: string, hour: number} {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: PARIS_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const part = (type: string) => parts.find(item => item.type === type)?.value ?? '';

    return {
      date: part('year') + '-' + part('month') + '-' + part('day'),
      hour: Number(part('hour')),
    };
  }

  private getDateAt(period: HourlyPeriod, dayIndex: number): string | null {
    return dayIndex < this.getDayCount(period) ? this.addDays(period.start, dayIndex) : null;
  }

  private toTime(date: string): number {
    return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
  }

  private addDays(date: string, days: number): string {
    return new Date(this.toTime(date) + days * DAY_MS).toISOString().slice(0, 10);
  }

  private daysBetween(start: string, end: string): number {
    return Math.round((this.toTime(end) - this.toTime(start)) / DAY_MS);
  }

  private formatDay(date: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(this.toTime(date)));
  }

  private loadPreferences(): void {
    let preferences: HourlyPreferences = {};

    try {
      preferences = JSON.parse(localStorage.getItem(PREFERENCES_KEY) ?? '{}') ?? {};
    } catch {
      preferences = {};
    }

    const metricKeys = this.metrics.map(metric => metric.key);

    if (Array.isArray(preferences.metrics)) {
      this.selectedMetricKeys = preferences.metrics.filter(key => metricKeys.includes(key));
    }

    if (preferences.chartMetric && metricKeys.includes(preferences.chartMetric)) {
      this.chartMetricKey = preferences.chartMetric;
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify({
        metrics: this.selectedMetricKeys,
        chartMetric: this.chartMetricKey,
      }));
    } catch {
    }
  }
}
